import { ReparoApi } from '@api/index';
import type { DataSheetCaptureData } from '@components/ui/DataSheet';
import type { PageCaptureSpec } from '@utils/headlessCapture';

/**
 * TABLE_CAPTURE = any DataSheet emailed as it stands. Send-now carries the grid's own cells and
 * picture; a schedule carries where the grid lives (`page`) and the worker reopens that page
 * headlessly at every send.
 */
export type ReportScheduleSourceKind = 'BUILTIN' | 'SAVED_REPORT' | 'ALERT' | 'TABLE_CAPTURE';
export type ReportScheduleBuiltinType =
    | 'INVENTORY_BY_SKU_BY_CHANNEL'
    | 'MATERIAL_STOCK_BY_FACILITY'
    /** Inventory → DoH View. */
    | 'DOH_BY_CHANNEL'
    /** Replenishment → Inventory health. */
    | 'INVENTORY_HEALTH_BY_SKU'
    /** Distribution → Channel Health. */
    | 'DISTRIBUTION_CHANNEL_HEALTH'
    /** Orders → lines with a goods-ready date and no brand QC verdict yet. */
    | 'PURCHASE_ORDER_QC_DUE';
export type ReportScheduleFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';
export type ReportScheduleRunStatus = 'SUCCESS' | 'FAILED';

/**
 * Category sentinel for materials with no category. Persisted into
 * `filters.categories` and understood by the backend generator, which mirrors
 * the literal in report-schedule.filters.ts — keep the two in lockstep.
 */
export const UNCATEGORIZED_CATEGORY = '__uncategorized__';

export interface ReportScheduleFilters {
    type?: string;
    categories?: string[];
    productGroups?: string[];
    tiers?: string[];
    isActive?: boolean;
    search?: string;
    /** MATERIAL_STOCK_BY_FACILITY only. */
    materialType?: 'RM' | 'PM';
    /** MATERIAL_STOCK_BY_FACILITY only. */
    facilityType?: 'WAREHOUSE' | 'CONTRACT_MANUFACTURER' | 'VENDOR';
    /** DOH_BY_CHANNEL / DISTRIBUTION_CHANNEL_HEALTH — the channel the view was pivoted on. */
    channelKey?: string;
    /** The DRR lookback the view was reading, in days. Every cover figure depends on it. */
    drrWindow?: number;
    /** Rate basis the view was reading. */
    basis?: 'FORWARD' | 'BACKWARD';
    /** INVENTORY_HEALTH_BY_SKU — finished goods or materials. */
    itemType?: 'product' | 'material';
    /** INVENTORY_HEALTH_BY_SKU — narrow to one supply model. */
    sourcingModel?: 'BUY' | 'MAKE' | 'UNMAPPED';
    /** PURCHASE_ORDER_QC_DUE — only lines with a goods-ready date and no brand QC verdict. */
    qcDueOnly?: boolean;
    /** PURCHASE_ORDER_QC_DUE — narrow to these vendors. */
    vendorIds?: string[];
    /**
     * Column keys the operator had hidden, so the emailed CSV carries the same column set.
     * Presentation only — hiding a column never changes which rows are in scope.
     */
    hiddenColumns?: string[];
    /** The grid's break-down (SKU explosion) toggle at capture time — a view transform. */
    exploded?: boolean;
}

export function isFiltersEmpty(filters: ReportScheduleFilters | null | undefined): boolean {
    if (!filters) return true;
    const t = filters.type;
    if (t && t !== 'OVERALL') return false;
    if (filters.categories?.length) return false;
    if (filters.productGroups?.length) return false;
    if (filters.tiers?.length) return false;
    if (filters.isActive !== undefined) return false;
    if (filters.channelKey) return false;
    if (filters.drrWindow !== undefined) return false;
    if (filters.basis) return false;
    if (filters.itemType) return false;
    if (filters.sourcingModel) return false;
    if (filters.hiddenColumns?.length) return false;
    if (filters.exploded) return false;
    if (filters.search && filters.search.trim().length > 0) return false;
    if (filters.materialType) return false;
    if (filters.facilityType) return false;
    return true;
}

export interface ReportScheduleFilterChip {
    key: string;
    label: string;
    dimension: keyof ReportScheduleFilters;
    value?: string | number | boolean | string[];
    /**
     * Shown but not removable.
     *
     * For inputs that are not scope filters: the rate basis, the DRR window and the hidden-column
     * set describe HOW the numbers were computed and which columns were on screen. Removing one
     * would not widen the report, it would produce a different one — silently re-based to the org
     * default, or with columns the operator had deliberately hidden.
     */
    fixed?: boolean;
}

const MATERIAL_TYPE_LABEL: Record<string, string> = {
    RM: 'Raw Material',
    PM: 'Packaging Material',
};

const FACILITY_TYPE_LABEL: Record<string, string> = {
    WAREHOUSE: 'Warehouses',
    CONTRACT_MANUFACTURER: 'Contract Manufacturers',
    VENDOR: 'Vendors',
};

/**
 * The single description of a schedule's frozen filters — rendered as
 * removable pills in the schedule modal and as read-only pills on the
 * Scheduled Emails page. Dimensions are disjoint per report type, so one
 * pass covers both the inventory and the RM/PM reports.
 */
export function describeScheduleFilters(
    filters: ReportScheduleFilters | null | undefined,
): ReportScheduleFilterChip[] {
    if (!filters) return [];
    const chips: ReportScheduleFilterChip[] = [];
    if (filters.type && filters.type !== 'OVERALL') {
        chips.push({
            key: `type:${filters.type}`,
            label: `Channel: ${filters.type}`,
            dimension: 'type',
            value: filters.type,
        });
    }
    if (filters.materialType) {
        chips.push({
            key: `materialType:${filters.materialType}`,
            label: `Type: ${MATERIAL_TYPE_LABEL[filters.materialType] ?? filters.materialType}`,
            dimension: 'materialType',
            value: filters.materialType,
        });
    }
    for (const cat of filters.categories ?? []) {
        chips.push({
            key: `cat:${cat}`,
            label: `Category: ${cat === UNCATEGORIZED_CATEGORY ? 'Uncategorized' : cat}`,
            dimension: 'categories',
            value: cat,
        });
    }
    if (filters.facilityType) {
        chips.push({
            key: `facilityType:${filters.facilityType}`,
            label: `Facilities: ${FACILITY_TYPE_LABEL[filters.facilityType] ?? filters.facilityType}`,
            dimension: 'facilityType',
            value: filters.facilityType,
        });
    }
    for (const pg of filters.productGroups ?? []) {
        chips.push({
            key: `pg:${pg}`,
            label: `Group: ${pg}`,
            dimension: 'productGroups',
            value: pg,
        });
    }
    for (const t of filters.tiers ?? []) {
        chips.push({
            key: `tier:${t}`,
            label: `Tier: ${t}`,
            dimension: 'tiers',
            value: t,
        });
    }
    if (filters.isActive !== undefined) {
        chips.push({
            key: 'isActive',
            label: filters.isActive ? 'Active only' : 'Inactive only',
            dimension: 'isActive',
            value: filters.isActive,
        });
    }
    if (filters.search && filters.search.trim().length > 0) {
        chips.push({
            key: 'search',
            label: `Search: "${filters.search}"`,
            dimension: 'search',
            value: filters.search,
        });
    }
    if (filters.channelKey) {
        chips.push({
            key: `channelKey:${filters.channelKey}`,
            label: `Pivot: ${filters.channelKey}`,
            dimension: 'channelKey',
            value: filters.channelKey,
        });
    }
    if (filters.itemType) {
        chips.push({
            key: `itemType:${filters.itemType}`,
            label: filters.itemType === 'material' ? 'Materials' : 'Finished goods',
            dimension: 'itemType',
            value: filters.itemType,
        });
    }
    if (filters.sourcingModel) {
        chips.push({
            key: `sourcingModel:${filters.sourcingModel}`,
            label: `Sourcing: ${filters.sourcingModel}`,
            dimension: 'sourcingModel',
            value: filters.sourcingModel,
        });
    }
    /*
     * The rate policy is shown but NOT removable: it is not a scope filter, it is the basis the
     * numbers were computed on. Dropping it would silently re-base every cover figure in the
     * report to the org default, which is a different report rather than a wider one.
     */
    if (filters.basis) {
        chips.push({
            key: `basis:${filters.basis}`,
            label: filters.basis === 'FORWARD' ? 'Rate: forecast' : 'Rate: past sales',
            dimension: 'basis',
            value: filters.basis,
            fixed: true,
        });
    }
    if (filters.drrWindow !== undefined) {
        chips.push({
            key: `drrWindow:${filters.drrWindow}`,
            label: `Rate window: ${filters.drrWindow}d`,
            dimension: 'drrWindow',
            value: filters.drrWindow,
            fixed: true,
        });
    }
    if (filters.exploded) {
        chips.push({
            key: 'exploded',
            label: 'Break-down: on',
            dimension: 'exploded',
            value: true,
            fixed: true,
        });
    }
    if (filters.hiddenColumns?.length) {
        chips.push({
            key: 'hiddenColumns',
            label: `${filters.hiddenColumns.length} column(s) hidden`,
            dimension: 'hiddenColumns',
            value: filters.hiddenColumns,
            fixed: true,
        });
    }
    return chips;
}

/** Drops one chip's value, clearing the dimension when it empties. */
export function removeScheduleFilter(
    filters: ReportScheduleFilters,
    chip: ReportScheduleFilterChip,
): ReportScheduleFilters {
    const out: ReportScheduleFilters = { ...filters };
    switch (chip.dimension) {
        case 'categories':
            out.categories = (out.categories ?? []).filter((c) => c !== chip.value);
            if (out.categories.length === 0) delete out.categories;
            break;
        case 'productGroups':
            out.productGroups = (out.productGroups ?? []).filter((p) => p !== chip.value);
            if (out.productGroups.length === 0) delete out.productGroups;
            break;
        case 'tiers':
            out.tiers = (out.tiers ?? []).filter((t) => t !== chip.value);
            if (out.tiers.length === 0) delete out.tiers;
            break;
        default:
            // Fixed chips have no remove button, so this is only reached for real scope
            // dimensions; deleting the key restores "no constraint" for that dimension.
            delete out[chip.dimension];
            break;
    }
    return out;
}

/** One named CSV attachment: a frozen filter set over the schedule's builtin report. */
export interface ReportAttachmentSpec {
    /** Client-generated id (crypto.randomUUID()), referenced by body blocks. */
    id: string;
    /** "DoH — Amazon_FBA". Also seeds the attachment filename prefix. */
    label: string;
    filters: ReportScheduleFilters;
    /** TABLE_CAPTURE only: the grid's own rendered cells; the XLSX is built from these verbatim. */
    table?: DataSheetCaptureData;
    /** Scheduled TABLE_CAPTURE: the page and sheet the worker reopens and serializes at send time. */
    page?: PageCaptureSpec;
}

/**
 * A declarative "what to screenshot" — re-rendered server-side at send time (slice 4).
 * Captured from live grid state, never authored as a form.
 */
export interface TableCaptureSpec {
    filters: ReportScheduleFilters;
    /** Scheduled TABLE_CAPTURE: the page and sheet the worker reopens and photographs. */
    page?: PageCaptureSpec;
    /** "Capture until row X." */
    rowLimit?: number;
    grouping?: {
        groupBy: string;
        /** Second grouping level, when the view had one. */
        subGroupBy?: string;
        /** The view's Sort By at capture time (e.g. TOP_SELLING); absent = default order. */
        sortBy?: string;
        /** 'ALL' / 'NONE'; partial expansion travels as the exact keys instead. */
        expanded?: 'ALL' | 'NONE';
        expandedKeys?: string[];
    };
    widthPx?: number;
}

/**
 * Ordered body blocks. TABLE_IMAGE and AI_SUMMARY are rejected by the backend until their
 * render slices land — the composer must keep them disabled ("coming soon").
 */
export type ReportBodyBlock =
    | { type: 'TEXT'; html: string }
    | {
          type: 'TABLE_IMAGE';
          /**
           * Browser-captured picture of the grid's exact view (data:image/jpeg;base64).
           * Send-now only — schedules reject it (a stored picture goes stale) until the
           * send-time headless render slice fills `capture` instead.
           */
          imageData?: string;
          capture?: TableCaptureSpec;
          caption?: string;
      }
    | {
          type: 'AI_SUMMARY';
          surfaceKey: string;
          attachmentId?: string;
          instructions?: string;
      };

export interface ReportSchedule {
    id: string;
    merchantId: string;
    createdByUserId: string;
    name: string;
    sourceKind: ReportScheduleSourceKind;
    builtinReportType?: ReportScheduleBuiltinType | null;
    savedReportId?: string | null;
    alertId?: string | null;
    recipients: string[];
    frequency: ReportScheduleFrequency;
    triggerTime: string;
    /** Days it sends on, 0 = Sunday. On DAILY, null = every day. */
    triggerDaysOfWeek?: number[] | null;
    triggerDayOfMonth?: number | null;
    timezone: string;
    enabled: boolean;
    lastRunAt?: string | null;
    lastRunStatus?: ReportScheduleRunStatus | null;
    lastRunError?: string | null;
    nextRunAt?: string | null;
    filters?: ReportScheduleFilters;
    /** Subject template ({{name}}, {{date}}); null → the default subject. */
    subject?: string | null;
    /** Empty → legacy single attachment from `filters`. */
    attachments?: ReportAttachmentSpec[];
    /** Empty → the generated default body (summary line + attachment manifest). */
    body?: ReportBodyBlock[];
    createdAt: string;
    updatedAt: string;
}

export interface CreateReportScheduleInput {
    name: string;
    sourceKind: ReportScheduleSourceKind;
    builtinReportType?: ReportScheduleBuiltinType;
    savedReportId?: string;
    alertId?: string;
    recipients: string[];
    frequency: ReportScheduleFrequency;
    triggerTime: string;
    triggerDaysOfWeek?: number[];
    triggerDayOfMonth?: number;
    enabled?: boolean;
    filters?: ReportScheduleFilters;
    subject?: string;
    attachments?: ReportAttachmentSpec[];
    body?: ReportBodyBlock[];
}

export interface UpdateReportScheduleInput {
    name?: string;
    recipients?: string[];
    frequency?: ReportScheduleFrequency;
    triggerTime?: string;
    triggerDaysOfWeek?: number[];
    triggerDayOfMonth?: number | null;
    enabled?: boolean;
    filters?: ReportScheduleFilters;
    /** Empty string clears back to the default subject. */
    subject?: string;
    /** Empty array reverts to the legacy single attachment from `filters`. */
    attachments?: ReportAttachmentSpec[];
    /** Empty array reverts to the generated default body. */
    body?: ReportBodyBlock[];
}

/**
 * A one-off send: the full email definition, dispatched immediately through the same
 * pipeline a schedule fires through. Nothing is persisted (audit row aside).
 */
export interface SendReportNowInput {
    name: string;
    sourceKind: ReportScheduleSourceKind;
    builtinReportType?: ReportScheduleBuiltinType;
    alertId?: string;
    recipients: string[];
    subject?: string;
    /** Legacy single-view convenience; ignored when `attachments` is non-empty. */
    filters?: ReportScheduleFilters;
    attachments?: ReportAttachmentSpec[];
    body?: ReportBodyBlock[];
}

export interface SendReportNowResult {
    recipients: string[];
    attachments: Array<{ label: string; filename: string; rowCount: number }>;
    totalRowCount: number;
}

/** Preview input — a send-now payload minus recipients; nothing is delivered. */
export type PreviewReportInput = Omit<SendReportNowInput, 'recipients'>;

export interface PreviewReportResult {
    subject: string;
    /** Full email HTML with inline images as data: URLs — render it in an iframe. */
    html: string;
    attachments: Array<{ label: string; filename: string; rowCount: number }>;
}

/**
 * The exact email a fire would produce — same generators, body pipeline and headless
 * image render as a real send — returned for display instead of delivered.
 */
export const previewReport = async (
    input: PreviewReportInput,
): Promise<PreviewReportResult> => {
    return ReparoApi<PreviewReportResult>({
        method: 'POST',
        url: '/report-schedules/preview',
        body: JSON.stringify(input),
    });
};

export const sendReportNow = async (
    input: SendReportNowInput,
): Promise<SendReportNowResult> => {
    return ReparoApi<SendReportNowResult>({
        method: 'POST',
        url: '/report-schedules/send-now',
        body: JSON.stringify(input),
    });
};

export const createReportSchedule = async (
    input: CreateReportScheduleInput,
): Promise<ReportSchedule> => {
    return ReparoApi<ReportSchedule>({
        method: 'POST',
        url: '/report-schedules',
        body: JSON.stringify(input),
    });
};

export const listReportSchedules = async (): Promise<ReportSchedule[]> => {
    return ReparoApi<ReportSchedule[]>({
        method: 'GET',
        url: '/report-schedules',
    });
};

export const updateReportSchedule = async (
    id: string,
    input: UpdateReportScheduleInput,
): Promise<ReportSchedule> => {
    return ReparoApi<ReportSchedule>({
        method: 'PATCH',
        url: `/report-schedules/${id}`,
        body: JSON.stringify(input),
    });
};

export const deleteReportSchedule = async (id: string): Promise<void> => {
    await ReparoApi<void>({
        method: 'DELETE',
        url: `/report-schedules/${id}`,
    });
};
